export type TimelineScale = "days" | "months" | "weeks";

export type TimelineDateSpan = {
  end: Date;
  start: Date;
};

export type TimelineColumn = {
  end: Date;
  isWeekend: boolean;
  key: string;
  label: string;
  monthKey: string;
  start: Date;
  subLabel: string;
};

export type TimelineRange = {
  end: Date;
  start: Date;
};

const dayMilliseconds = 24 * 60 * 60 * 1000;

export const timelineScaleLabels: Record<TimelineScale, string> = {
  days: "Days",
  months: "Months",
  weeks: "Weeks",
};

export const timelineCellWidths: Record<TimelineScale, number> = {
  days: 56,
  months: 184,
  weeks: 144,
};

export function parseTimelineDate(value: string | null | undefined) {
  if (!value) {
    return null;
  }

  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return null;
  }

  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return Number.isNaN(date.getTime()) ? null : date;
}

export function todayTimelineDate(now = new Date()) {
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
}

export function buildTimelineViewportRange(scale: TimelineScale, center = todayTimelineDate()): TimelineRange {
  const centerDate = startOfDay(center);
  if (scale === "days") {
    return { start: startOfDay(addTimelineDays(centerDate, -30)), end: startOfDay(addTimelineDays(centerDate, 90)) };
  }
  if (scale === "weeks") {
    return { start: startOfWeek(addWeeks(centerDate, -16)), end: addWeeks(startOfWeek(centerDate), 32) };
  }
  return { start: startOfMonth(addMonths(centerDate, -12)), end: addMonths(startOfMonth(centerDate), 24) };
}

export function shiftTimelineRange(range: TimelineRange, scale: TimelineScale, direction: "backward" | "forward"): TimelineRange {
  const amount = scale === "days" ? 60 : 18;
  const signedAmount = direction === "backward" ? -amount : amount;
  return {
    end: scale === "days" ? addTimelineDays(range.end, signedAmount) : scale === "weeks" ? addWeeks(range.end, signedAmount) : addMonths(range.end, signedAmount),
    start: scale === "days" ? addTimelineDays(range.start, signedAmount) : scale === "weeks" ? addWeeks(range.start, signedAmount) : addMonths(range.start, signedAmount),
  };
}

export function packTimelineLanes<T>(items: Array<{ end: Date; item: T; start: Date }>) {
  const lanes: Array<Array<{ end: Date; item: T; start: Date }>> = [];
  const sortedItems = [...items].sort((first, second) => first.start.getTime() - second.start.getTime() || second.end.getTime() - first.end.getTime());

  for (const item of sortedItems) {
    const lane = lanes.find((candidate) => {
      const last = candidate[candidate.length - 1];
      return last && last.end < item.start;
    });
    if (lane) lane.push(item);
    else lanes.push([item]);
  }

  return lanes;
}

export function buildTimelineColumns(range: TimelineRange, scale: TimelineScale): TimelineColumn[] {
  const columns: TimelineColumn[] = [];
  const monthFormatter = new Intl.DateTimeFormat(undefined, { month: "short", timeZone: "UTC" });
  const weekdayFormatter = new Intl.DateTimeFormat(undefined, { weekday: "short", timeZone: "UTC" });
  let cursor = range.start;
  let index = 0;

  while (cursor < range.end && index < 2_000) {
    const end = scale === "days" ? addTimelineDays(cursor, 1) : scale === "weeks" ? addWeeks(cursor, 1) : addMonths(cursor, 1);
    const monthKey = `${cursor.getUTCFullYear()}-${String(cursor.getUTCMonth() + 1).padStart(2, "0")}`;
    const weekEnd = addTimelineDays(end, -1);

    columns.push({
      end,
      isWeekend: scale === "days" && [0, 6].includes(cursor.getUTCDay()),
      key: `${scale}:${cursor.toISOString()}`,
      label:
        scale === "days"
          ? String(cursor.getUTCDate())
          : scale === "weeks"
            ? cursor.getUTCMonth() === weekEnd.getUTCMonth()
              ? `${monthFormatter.format(cursor)} ${cursor.getUTCDate()} – ${weekEnd.getUTCDate()}`
              : `${monthFormatter.format(cursor)} ${cursor.getUTCDate()} – ${monthFormatter.format(weekEnd)} ${weekEnd.getUTCDate()}`
            : monthFormatter.format(cursor),
      monthKey,
      start: cursor,
      subLabel:
        scale === "days"
          ? weekdayFormatter.format(cursor).slice(0, 2)
          : scale === "weeks"
            ? `W${isoWeekNumber(cursor)}`
            : String(cursor.getUTCFullYear()),
    });

    cursor = end;
    index += 1;
  }

  return columns;
}

export function timelinePosition(date: Date, rangeStart: Date, scale: TimelineScale) {
  const normalized = startOfDay(date);
  const dayOffset = (normalized.getTime() - rangeStart.getTime()) / dayMilliseconds;

  if (scale === "days") {
    return dayOffset;
  }

  if (scale === "weeks") {
    return dayOffset / 7;
  }

  const monthOffset = (normalized.getUTCFullYear() - rangeStart.getUTCFullYear()) * 12 + normalized.getUTCMonth() - rangeStart.getUTCMonth();
  const monthLength = daysInMonth(normalized.getUTCFullYear(), normalized.getUTCMonth());
  return monthOffset + (normalized.getUTCDate() - 1) / monthLength;
}

// Returns the day the position falls WITHIN (floor semantics): a pointer
// anywhere inside a day cell resolves to that day, and the date only advances
// when the pointer crosses the cell boundary — not at the cell midpoint.
export function timelineDateAtPosition(position: number, rangeStart: Date, scale: TimelineScale) {
  if (scale === "days") {
    return startOfDay(addTimelineDays(rangeStart, Math.floor(position)));
  }

  if (scale === "weeks") {
    return startOfDay(addTimelineDays(rangeStart, Math.floor(position * 7)));
  }

  const monthOffset = Math.floor(position);
  const monthStart = startOfMonth(addMonths(rangeStart, monthOffset));
  const monthLength = daysInMonth(monthStart.getUTCFullYear(), monthStart.getUTCMonth());
  const dayOffset = Math.min(monthLength - 1, Math.max(0, Math.floor((position - monthOffset) * monthLength)));
  return startOfDay(addTimelineDays(monthStart, dayOffset));
}

export function moveTimelineSpan(span: TimelineDateSpan, amount: number): TimelineDateSpan {
  return { end: addTimelineDays(span.end, amount), start: addTimelineDays(span.start, amount) };
}

export function resizeTimelineSpan(span: TimelineDateSpan, edge: "end" | "start", date: Date): TimelineDateSpan {
  if (edge === "start") {
    return { end: span.end, start: date > span.end ? span.end : date };
  }

  return { end: date < span.start ? span.start : date, start: span.start };
}

export function timelineSpanEquals(first: TimelineDateSpan, second: TimelineDateSpan) {
  return first.start.getTime() === second.start.getTime() && first.end.getTime() === second.end.getTime();
}

export function timelineBarPosition(span: TimelineDateSpan, range: TimelineRange, scale: TimelineScale) {
  const start = timelinePosition(span.start, range.start, scale);
  const end = timelinePosition(addTimelineDays(span.end, 1), range.start, scale);
  return {
    left: start,
    width: Math.max(end - start, scale === "months" ? 0.08 : scale === "weeks" ? 1 / 7 : 1),
  };
}

export function formatTimelineDate(date: Date) {
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" }).format(date);
}

export function formatTimelineDateShort(date: Date) {
  return new Intl.DateTimeFormat(undefined, { day: "numeric", month: "short", timeZone: "UTC" }).format(date);
}

export function formatTimelineDateSpan(span: TimelineDateSpan | null) {
  if (!span) {
    return "No dates";
  }
  const start = formatTimelineDate(span.start);
  const end = formatTimelineDate(span.end);
  return start === end ? start : `${start} – ${end}`;
}

export function addTimelineDays(date: Date, amount: number) {
  return new Date(date.getTime() + amount * dayMilliseconds);
}

function startOfDay(date: Date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function startOfWeek(date: Date) {
  const day = date.getUTCDay();
  return startOfDay(addTimelineDays(date, day === 0 ? -6 : 1 - day));
}

function startOfMonth(date: Date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));
}

function addWeeks(date: Date, amount: number) {
  return addTimelineDays(date, amount * 7);
}

function addMonths(date: Date, amount: number) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + amount, date.getUTCDate()));
}

function daysInMonth(year: number, month: number) {
  return new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
}

function isoWeekNumber(date: Date) {
  const nearestThursday = startOfDay(date);
  const weekday = nearestThursday.getUTCDay() || 7;
  nearestThursday.setUTCDate(nearestThursday.getUTCDate() + 4 - weekday);
  const yearStart = new Date(Date.UTC(nearestThursday.getUTCFullYear(), 0, 1));
  return Math.ceil(((nearestThursday.getTime() - yearStart.getTime()) / dayMilliseconds + 1) / 7);
}

export function toTimelineDateInput(date: Date) {
  return date.toISOString().slice(0, 10);
}
